/**
 * Get value severity based on thresholds
 * @param {number} value - The current value
 * @param {Object} thresholds - The threshold configuration
 * @returns {string} - ideal, warning, critical or empty
 */
export const getValueSeverity = (value, thresholds) => {
  if (value === null || value === undefined || value === 0) return '';

  if (value < thresholds.min) return 'critical';
  if (value > thresholds.max) return 'critical';
  if (value >= thresholds.idealMin && value <= thresholds.idealMax) return 'ideal';
  if (value < thresholds.idealMin) return 'warning';
  if (value > thresholds.idealMax) return 'warning';

  return '';
};

/**
 * Get value status class based on thresholds
 * @param {number} value - The current value
 * @param {Object} thresholds - The threshold configuration
 * @returns {string} - CSS class name for the status
 */
export const getValueStatus = (value, thresholds) => {
  const severity = getValueSeverity(value, thresholds);

  if (severity === 'critical') return 'value-status-red';
  if (severity === 'warning') return 'value-status-yellow';
  if (severity === 'ideal') return 'value-status-green';
  return '';
};

/**
 * Format timestamp for display
 * @param {string|number|Date} ts - The timestamp to format
 * @returns {string} - Formatted timestamp string
 */
export const formatTimestamp = (ts) => {
  if (!ts) return '-';
  const date = new Date(ts);
  return date.toLocaleString('es-ES', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
};

/**
 * Format short timestamp (without year)
 * @param {string|number|Date} ts - The timestamp to format
 * @returns {string} - Formatted timestamp string
 */
export const formatShortTimestamp = (ts) => {
  if (!ts) return 'Sin datos';
  const date = new Date(ts);
  return date.toLocaleString('es-ES', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
};

/**
 * Get time since last update as human readable string
 * @param {number} lastUpdate - Timestamp of last update
 * @param {number} currentTime - Current timestamp
 * @returns {string} - Human readable time string
 */
export const getTimeSince = (lastUpdate, currentTime) => {
  if (!lastUpdate) return '-';
  const seconds = Math.floor((currentTime - lastUpdate) / 1000);

  if (seconds <= 0) return 'Ahora';
  if (seconds < 60) return `Hace ${seconds}s`;
  if (seconds < 3600) return `Hace ${Math.floor(seconds / 60)}m`;
  return `Hace ${Math.floor(seconds / 3600)}h`;
};

/**
 * Get refresh interval as display text
 * @param {number} interval - Interval in seconds
 * @returns {string} - Display text
 */
export const getRefreshIntervalText = (interval) => {
  if (interval === 1) return '1s';
  if (interval < 60) return `${interval}s`;
  return `${interval / 60}m`;
};

/**
 * Calculate trend between current and previous values
 * @param {Object} current - Current data object
 * @param {Object} previous - Previous data object
 * @param {string} key - The key to compare
 * @returns {number} - The difference
 */
export const getTrend = (current, previous, key) => {
  if (!previous) return 0;
  return (current?.[key] || 0) - (previous?.[key] || 0);
};

/**
 * Calculate statistics for a data array
 * @param {Array} data - Array of data objects
 * @param {string} key - The key to calculate stats for
 * @returns {Object} - Object with avg, min, max values
 */
export const calculateStats = (data, key) => {
  if (!data || data.length === 0) return { avg: null, min: null, max: null, count: 0 };

  const values = data.map(item => item[key]).filter(v => v !== null && v !== undefined && v !== 0);
  if (values.length === 0) return { avg: null, min: null, max: null, count: 0 };

  return {
    avg: values.reduce((sum, val) => sum + val, 0) / values.length,
    min: Math.min(...values),
    max: Math.max(...values),
    count: values.length,
  };
};

/**
 * Get all statistics for sensor data
 * @param {Array} history - History array of sensor data
 * @returns {Object|null} - Statistics object or null
 */
export const getAllStats = (history) => {
  if (!history || history.length === 0) return null;

  return {
    temp1: calculateStats(history, 'temp1'),
    temp2: calculateStats(history, 'temp2'),
    do1: calculateStats(history, 'do1_mgL'),
    do2: calculateStats(history, 'do2_mgL'),
    sat1: calculateStats(history, 'sat1_pct'),
    sat2: calculateStats(history, 'sat2_pct'),
  };
};
